/**
 * Page header used at the top of every primary view. Sticky, holds the
 * numbered eyebrow, the H1, optional right-side actions, and an optional
 * tab strip below.
 *
 * Reference: spec section 8.5 and the design-system numbered-eyebrow
 * pattern.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  /** Two-digit prefix shown in orange before the section word. */
  eyebrowNumber: string;
  /** Uppercase section word that follows the number. */
  eyebrowText: string;
  /** Large display heading. */
  title: string;
  /** Optional right-aligned slot for actions like New deal / Import. */
  rightSlot?: ReactNode;
  /** Optional tab strip rendered below the title row. */
  tabs?: ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrowNumber,
  eyebrowText,
  title,
  rightSlot,
  tabs,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'sticky top-0 z-10 -mx-mx-6 mb-mx-5 border-b border-mx-grey-800 bg-mx-near-black px-mx-6 pb-mx-3 pt-mx-5',
        className,
      )}
    >
      <p className="eyebrow mono-label text-mx-grey-mid">
        <span className="num">{eyebrowNumber}</span>
        {eyebrowText}
      </p>
      <div className="mt-mx-2 flex items-end justify-between gap-mx-4">
        <h1 className="font-display text-mx-h1 font-bold text-mx-white">{title}</h1>
        {rightSlot && <div>{rightSlot}</div>}
      </div>
      {tabs && <div className="mt-mx-4">{tabs}</div>}
    </header>
  );
}
